import { useLayoutEffect } from 'react'
import type { ChartElementRef } from './statisticsChartUtils'

type SeriesCutoff = {
  lastIndex: number
  endPosition: number
  values: number[]
}

// Chart treats missing values as zero. Hide future points and clip the native
// lines, reposition partial-month endpoints, and separate axis ticks from the
// precise reporting dates used in the data table and tooltips.
export const useChartLineCutoff = (
  chart: ChartElementRef | null,
  taken: SeriesCutoff,
  showPlanned: boolean,
  axisLabels: string[],
  planned?: SeriesCutoff
) => {
  useLayoutEffect(() => {
    const root = chart?.shadowRoot
    if (!root) return

    const clipSeries = (
      lineIndex: number,
      lastIndex: number,
      endPosition = lastIndex
    ) => {
      const line =
        root.querySelectorAll<HTMLElement>('.lineContainer')[lineIndex]
      if (!line) return
      const points = line.querySelectorAll<HTMLElement>('.linePoint')
      const svg = line.querySelector<SVGSVGElement>('svg')
      line.style.visibility = lastIndex < 0 ? 'hidden' : ''
      if (svg) {
        const remaining =
          100 * (1 - Math.max(0, endPosition) / Math.max(1, points.length - 1))
        svg.style.clipPath =
          endPosition < points.length - 1
            ? `inset(-1% ${remaining}% -1% -1%)`
            : ''
      }
      points.forEach((point, index) => {
        const hidden = index > lastIndex
        point.style.visibility = hidden ? 'hidden' : ''
        point.style.translate = ''
        point.tabIndex = hidden ? -1 : 0
        if (hidden) point.setAttribute('aria-hidden', 'true')
        else point.removeAttribute('aria-hidden')
      })
      return { line, points, svg }
    }

    const applySeries = (lineIndex: number, cutoff: SeriesCutoff) => {
      const series = clipSeries(lineIndex, cutoff.lastIndex, cutoff.endPosition)
      if (!series) return
      const { line, points, svg } = series
      const endpoint = points[cutoff.lastIndex]
      if (cutoff.endPosition === cutoff.lastIndex) return
      const total = Number.parseFloat(
        root
          .querySelector<HTMLElement>('.axis')
          ?.style.getPropertyValue('--total') ?? ''
      )
      if (!svg || !Number.isFinite(total) || total <= 0) return
      const segments = Math.max(1, points.length - 1)
      // The native category axis is monthly. Keep completed months unchanged,
      // but put the endpoint and its marker on the actual reporting boundary.
      svg.querySelector('path.line')?.setAttribute(
        'd',
        cutoff.values
          .slice(0, cutoff.lastIndex + 1)
          .map((value, index) => {
            const position =
              index === cutoff.lastIndex ? cutoff.endPosition : index
            return `${index === 0 ? 'M' : 'L'}${(100 * position) / segments},${100 - (100 * value) / total}`
          })
          .join(' ')
      )
      if (endpoint) {
        line.style.containerType = 'inline-size'
        endpoint.style.translate = `${(100 * (cutoff.endPosition - cutoff.lastIndex)) / segments}cqw 0`
      }
    }

    const applyCutoff = () => {
      root.querySelectorAll('.lineContainer').forEach((line) => {
        line.querySelectorAll('[data-label]').forEach((tick, index) => {
          tick.setAttribute('data-label', axisLabels[index] ?? '')
        })
      })
      applySeries(showPlanned ? 1 : 0, taken)
      if (planned) applySeries(0, planned)
    }

    applyCutoff()
    const observer = new MutationObserver(applyCutoff)
    observer.observe(root, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [chart, taken, showPlanned, axisLabels, planned])
}
